import React, { useState } from "react"
import { SearchIcon } from "lucide-react"
import { useSecurities } from "../../api/securities"

interface SecuritiesTabProps {
  communicationId: string
  selectedSecurities: string[]
  onSecuritySelectionChange: (selectedIds: string[]) => void
}

const SecuritiesTab: React.FC<SecuritiesTabProps> = ({
  communicationId,
  selectedSecurities,
  onSecuritySelectionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("")

  const {
    data: securities,
    isLoading,
    error,
  } = useSecurities({
    managed: true,
    quadrant: true,
  })

  const filteredSecurities = securities
    ? securities.filter((security) =>
        security.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  const handleSecurityToggle = (id: string) => {
    if (selectedSecurities.includes(id)) {
      onSecuritySelectionChange(selectedSecurities.filter((sId) => sId !== id))
    } else {
      onSecuritySelectionChange([...selectedSecurities, id])
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Select all filtered securities
      const allIds = filteredSecurities.map((security) => security.id)
      onSecuritySelectionChange(allIds)
    } else {
      // Deselect all
      onSecuritySelectionChange([])
    }
  }

  const areAllSelected =
    filteredSecurities.length > 0 &&
    filteredSecurities.every((security) =>
      selectedSecurities.includes(security.id),
    )

  return (
    <div className="securities-tab">
      <h2 className="tab-heading">Securities to Exclude</h2>
      <p className="tab-subtext">
        Select the securities you want to <strong>exclude</strong> from the
        written report if any:
      </p>

      <div className="search-container">
        <label htmlFor="securitySearch">Search Securities</label>
        <div className="search-input-wrapper">
          <SearchIcon size={18} className="search-icon" />
          <input
            id="securitySearch"
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="selected-count">
        {selectedSecurities.length} securities selected
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading securities...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="text-danger">
            Error loading securities. Please try again.
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="securities-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={areAllSelected}
                    onChange={handleSelectAll}
                    disabled={filteredSecurities.length === 0}
                  />
                </th>
                <th>Exclude All</th>
              </tr>
            </thead>
            <tbody>
              {filteredSecurities.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center">
                    {searchTerm
                      ? "No securities match your search"
                      : "No securities available"}
                  </td>
                </tr>
              ) : (
                filteredSecurities.map((security) => (
                  <tr
                    key={security.id}
                    className={
                      selectedSecurities.includes(security.id) ? "selected" : ""
                    }
                  >
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedSecurities.includes(security.id)}
                        onChange={() => handleSecurityToggle(security.id)}
                      />
                    </td>
                    <td>{security.name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default SecuritiesTab
